import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import {
  buildStrategy,
  confirmStrategyBet,
  getStrategyRuns,
  getStrategyRun,
  deleteStrategyRun,
  reviewStrategyRun,
  repriceStrategyRun,
  fitCalibration,
  getBetfairStatus,
  getThreads,
  StrategyRun,
  StagedBet,
  Thread,
  ChatScope,
  ChatRef,
} from '../api/strategy';
import SectionChat from './SectionChat';
// The races we DIDN'T bet. This page shows the slate — a filtered view of the model's opinion.
// "3 bets from 26 races" is either excellent discipline or a broken gate, and the SELECTIONS look
// identical in both cases; the races we passed are the evidence that tells them apart.
import RaceDetailTable from './RaceDetailTable';
import { renderRich } from '../richText';
import './ClaudeStrategy.css';

interface Race {
  race_number: number;
  race_name: string;
  race_distance: number;
}
interface Meeting {
  _id: string;
  meeting_name: string;
  meeting_date: string;
  races: Race[];
  metro?: boolean;
}

const todayStr = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()); // Sydney race day, not UTC

// Remember the last opened run so it survives a refresh / leaving the tab.
const LAST_RUN_KEY = 'cs_last_run_id';

const confidenceClass = (c: string | null) =>
  c === 'high' ? 'conf-high' : c === 'medium' ? 'conf-med' : c === 'low' ? 'conf-low' : '';

const ClaudeStrategy: React.FC = () => {
  const [date, setDate] = useState<string>(todayStr());
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);

  const [bank, setBank] = useState({ current: 1000, running_pnl: 0, stop_loss: 200 });
  // The bank "current" is read-only and mirrors the live Betfair available balance.
  // null = not yet loaded / account not connected (we then fall back to a manual figure).
  const [liveBank, setLiveBank] = useState<number | null>(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [minStake, setMinStake] = useState(5); // Betfair exchange minimum (AUD)
  // v2.4 Change 9 — the COMMITMENT slider: the share of the live Betfair balance that
  // becomes today's bank. today's bank = live balance × this; every stake sizes off it.
  // Defaults to the whole balance (commit 100%). Flows to build / reprice as commitment_pct.
  const [riskPct, setRiskPct] = useState(100);
  // Operator-selectable staking plan: 'flat_fraction' = a fixed % of the live bank per
  // bet (the strategy default), 'flat' = a fixed-unit staked plan ($/bet). Flows to
  // build as stake_mode / flat_unit. Kelly stays calibration-gated server-side.
  const [stakeMode, setStakeMode] = useState<'flat_fraction' | 'flat'>('flat_fraction');
  const [flatUnit, setFlatUnit] = useState(50); // fixed $/bet for the staked-plan mode
  const [includeNotes, setIncludeNotes] = useState(true);
  // Scope the daily run to metro meetings only. Default OFF — the engine runs one
  // call per meeting and merges, so it can cover EVERY meeting without dropping any.
  // Turn on for a faster, cheaper metro-only run.
  const [metroOnly, setMetroOnly] = useState(false);

  const [run, setRun] = useState<StrategyRun | null>(null);
  const [building, setBuilding] = useState(false);
  // Whether the "re-run replaces today's picks" confirmation modal is open.
  const [confirmRebuild, setConfirmRebuild] = useState(false);
  const [opening, setOpening] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  // Which staged bet is awaiting the "place real money?" confirmation modal.
  const [confirmBetIdx, setConfirmBetIdx] = useState<number | null>(null);
  const [confirmErr, setConfirmErr] = useState<string | null>(null);
  // Take SP by default: work the strike price, roll the unmatched remainder into
  // the Betfair Starting Price pool at the jump. Uncheck to cancel it instead.
  const [takeSP, setTakeSP] = useState(true);
  // Re-price-to-live-market action state + the last summary banner it returned.
  const [repricing, setRepricing] = useState(false);
  const [repriceMsg, setRepriceMsg] = useState<string | null>(null);
  // v2.3 Ch1 — empirical calibration tuning from settled results.
  const [tuning, setTuning] = useState(false);
  const [tuneMsg, setTuneMsg] = useState<string | null>(null);
  // Manual per-bet overrides of price & stake (keyed by bet index). Empty string =
  // "use the staged value". These flow into the confirm call as { price, stake }.
  const [edits, setEdits] = useState<Record<number, { price?: string; stake?: string }>>({});
  const [history, setHistory] = useState<StrategyRun[]>([]);

  // Staking-Separation Spec — Button 2. Selection (Button 1) produces an unstaked pick
  // list; the user opts picks out and routes each to a platform, then "Run staking" sizes
  // the survivors (65/35 split, On-Base Dutched, Value quarter-Kelly). Recomputed each press.
  const [stakingBusy, setStakingBusy] = useState(false);
  const [stakeErr, setStakeErr] = useState<string | null>(null);
  // Picks opted out before staking (by bet index), and per-pick platform routing.
  const [deselected, setDeselected] = useState<Set<number>>(new Set());
  const [routes, setRoutes] = useState<Record<number, 'BETFAIR' | 'TAB' | 'CASH'>>({});

  // Section conversations (opt-in): which panels are open + loaded threads.
  const [threads, setThreads] = useState<Thread[]>([]);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  // Which bets have their "Why this pick" decision-logic panel open.
  const [openDetails, setOpenDetails] = useState<Set<number>>(new Set());
  // v2.4 Change 9 — the bet slate: a sortable flat view (time / price / edge) of the
  // day's bets showing edge, price and dollar stake together. Open by default.
  const [slateSort, setSlateSort] = useState<'time' | 'price' | 'edge' | 'track'>('time');
  const [showSlate, setShowSlate] = useState(true);

  const toggleDetails = (idx: number) =>
    setOpenDetails((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const refreshThreads = async (runId: string) => {
    try {
      setThreads(await getThreads(runId));
    } catch {
      /* non-fatal */
    }
  };

  const toggleSection = (key: string) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const findThread = (scope: ChatScope, ref: ChatRef): Thread | undefined =>
    threads.find((t) => {
      if (t.scope !== scope) return false;
      if (scope === 'day') return true;
      if (scope === 'bet') return t.ref?.betIndex === ref.betIndex;
      return t.ref?.track === ref.track && t.ref?.race === ref.race;
    });

  const onThreadUpdated = (t: Thread) =>
    setThreads((prev) => {
      const i = prev.findIndex((x) => x.ref_key === t.ref_key);
      if (i === -1) return [...prev, t];
      const next = prev.slice();
      next[i] = t;
      return next;
    });

  const loadMeetings = async (d: string) => {
    setLoadingMeetings(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/meetings/date/${d}`);
      const data: Meeting[] = Array.isArray(res.data) ? res.data : res.data?.meetings || [];
      setMeetings(data.filter((m) => (m.races?.length ?? 0) > 0));
    } catch (e: any) {
      setError(e?.message || 'Failed to load meetings');
      setMeetings([]);
    } finally {
      setLoadingMeetings(false);
    }
  };

  const loadHistory = async () => {
    try {
      setHistory(await getStrategyRuns(10));
    } catch {
      /* non-fatal */
    }
  };

  // The bank is the live Betfair available balance (read-only). Pull it on load and
  // after actions that move money so staking is always sized off the real balance.
  const loadBank = async () => {
    setBankLoading(true);
    try {
      const s = await getBetfairStatus();
      if (s?.account && Number.isFinite(Number(s.account.balance))) {
        const bal = Number(s.account.balance);
        setLiveBank(bal);
        setBank((b) => ({ ...b, current: bal }));
      }
      if (Number.isFinite(Number(s?.minStake))) setMinStake(Number(s.minStake));
    } catch {
      // No connected account / offline — leave liveBank null and fall back to the
      // last known figure (the build still sizes off the server's own live fetch).
      setLiveBank(null);
    } finally {
      setBankLoading(false);
    }
  };

  // There is one canonical run per day. When the date changes, open that day's
  // frozen run if it exists; otherwise clear the panel back to the build prompt.
  const loadRunForDate = async (d: string) => {
    try {
      const runs = await getStrategyRuns(20);
      setHistory(runs);
      const match = runs.find((r) => r.date === d);
      if (match) {
        await openRun(match.id);
      } else {
        setRun(null);
        setThreads([]);
        setOpenSections(new Set());
        setOpenDetails(new Set());
        try { localStorage.removeItem(LAST_RUN_KEY); } catch { /* ignore */ }
      }
    } catch {
      /* non-fatal */
    }
  };

  // Delete a saved run (e.g. clearing out test runs). Cascades on the server.
  // Uses an in-app confirm modal rather than window.confirm.
  const doDelete = async () => {
    const id = confirmDeleteId;
    if (!id) return;
    setDeleting(true);
    try {
      await deleteStrategyRun(id);
      setHistory((prev) => prev.filter((h) => h.id !== id));
      if (run?.id === id) {
        setRun(null);
        setThreads([]);
        try { localStorage.removeItem(LAST_RUN_KEY); } catch { /* ignore */ }
      }
      setConfirmDeleteId(null);
    } catch (e: any) {
      setError(e?.message || 'Could not delete that run');
    } finally {
      setDeleting(false);
    }
  };

  // Close the open strategy — clears the right panel back to the empty state.
  // Does not delete the run; it stays in Recent runs and can be reopened.
  const closeRun = () => {
    setRun(null);
    setThreads([]);
    setOpenSections(new Set());
    setOpenDetails(new Set());
    setError(null);
    try { localStorage.removeItem(LAST_RUN_KEY); } catch { /* ignore */ }
  };

  // Reopen a saved run (from the Recent runs table or restored on load).
  const openRun = async (id: string) => {
    setOpening(true);
    setError(null);
    setOpenSections(new Set());
    setOpenDetails(new Set());
    try {
      const r = await getStrategyRun(id);
      setRun(r);
      refreshThreads(r.id);
      try { localStorage.setItem(LAST_RUN_KEY, r.id); } catch { /* ignore */ }
    } catch (e: any) {
      setError(e?.message || 'Could not reopen that run');
      try { localStorage.removeItem(LAST_RUN_KEY); } catch { /* ignore */ }
    } finally {
      setOpening(false);
    }
  };

  // Load the day's card + that day's run when the date changes.
  useEffect(() => {
    loadMeetings(date);
    loadRunForDate(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Pull the live Betfair balance once on load (the bank is read-only = this figure).
  useEffect(() => {
    loadBank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The server reads the real balance at generation and returns it on the run; mirror
  // the LIVE balance + the commitment % back so the panel matches what was used.
  useEffect(() => {
    // v2.4 Change 9 — bank.current is the COMMITTED figure; bank.live_balance is the
    // real account balance. Show the live balance and re-derive the slider from it.
    const live = run?.bank?.live_balance ?? run?.bank?.current;
    if (Number.isFinite(Number(live))) {
      setLiveBank(Number(live));
      setBank((b) => ({ ...b, current: Number(live) }));
    }
    const cm = run?.bank?.commitment_pct;
    if (Number.isFinite(Number(cm)) && Number(cm) > 0) setRiskPct(Math.round(Number(cm) * 1000) / 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.bank?.live_balance, run?.bank?.current, run?.bank?.commitment_pct]);

  // Which meetings the run will actually consider: metro by default, falling back
  // to the full card when the date has no metro meetings.
  const metroMeetings = useMemo(() => meetings.filter((m) => m.metro), [meetings]);
  const shownMeetings = useMemo(
    () => (metroOnly && metroMeetings.length > 0 ? metroMeetings : meetings),
    [metroOnly, metroMeetings, meetings]
  );
  const raceCount = useMemo(
    () => shownMeetings.reduce((n, m) => n + (m.races?.length ?? 0), 0),
    [shownMeetings]
  );

  // Build (or, with replace=true, deliberately re-run and supersede) the day's
  // run over the whole card. No per-race selection — the server enumerates the
  // entire day for the date.
  const onBuild = async (replace = false) => {
    setBuilding(true);
    setError(null);
    setConfirmRebuild(false);
    if (!replace) setRun(null);
    try {
      const result = await buildStrategy(
        date,
        // v2.4 Change 9 — the slider is the COMMITMENT: the share of the live balance
        // that becomes today's bank. The per-bet flat fraction (and the caps) are the
        // engine's own levers (server defaults), sized off this committed figure.
        {
          ...bank,
          commitment_pct: riskPct / 100,
          // Operator-chosen staking plan for this run.
          stake_mode: stakeMode,
          ...(stakeMode === 'flat' ? { flat_unit: flatUnit } : {}),
        },
        includeNotes,
        replace,
        metroOnly
      );
      setRun(result);
      setThreads([]);
      setOpenSections(new Set());
      setOpenDetails(new Set());
      setDeselected(new Set());
      setRoutes({});
      setStakeErr(null);
      try { localStorage.setItem(LAST_RUN_KEY, result.id); } catch { /* ignore */ }
      refreshThreads(result.id);
      loadHistory();
    } catch (e: any) {
      setError(e?.message || 'Build failed');
    } finally {
      setBuilding(false);
    }
  };

  // Button 2 — Run staking. Sizes the post-opt-out pick list over the unified path.
  // Recomputed from scratch each press, so changing the bank / opt-out / platform and
  // pressing again re-stakes the whole list. Nothing is placed.
  const onStake = async () => {
    if (!run) return;
    setStakingBusy(true);
    setStakeErr(null);
    setError(null);
    try {
      const { stakeStrategyRun } = await import('../api/strategy');
      const { run: updated } = await stakeStrategyRun(run.id, {
        commitment_pct: riskPct / 100,
        deselected: [...deselected],
        routes,
      });
      setRun(updated);
      setEdits({});
      loadHistory();
    } catch (e: any) {
      setStakeErr(e?.message || 'Staking failed');
    } finally {
      setStakingBusy(false);
    }
  };

  const toggleDeselect = (idx: number) =>
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  const setRoute = (idx: number, platform: 'BETFAIR' | 'TAB' | 'CASH') =>
    setRoutes((prev) => ({ ...prev, [idx]: platform }));

  // Open the "place real money?" confirmation for a staged bet.
  const askConfirm = (idx: number) => {
    setConfirmErr(null);
    setError(null);
    setTakeSP(true); // default each confirmation back to taking SP
    setConfirmBetIdx(idx);
  };

  // Actually place the bet on Betfair (called from the confirmation modal). Sends any
  // manual price/stake override the user typed inline; otherwise the server uses the
  // staged figures (and re-sizes the Kelly stake off the live bank).
  const onConfirm = async (idx: number) => {
    if (!run) return;
    setConfirming(idx);
    setConfirmErr(null);
    setError(null);
    try {
      const e = edits[idx] || {};
      const price = e.price != null && e.price !== '' ? Number(e.price) : undefined;
      const stake = e.stake != null && e.stake !== '' ? Number(e.stake) : undefined;
      if (stake != null && (!Number.isFinite(stake) || stake < minStake)) {
        setConfirmErr(`Stake must be at least ${fmtMoney(minStake)} (Betfair minimum).`);
        setConfirming(null);
        return;
      }
      await confirmStrategyBet(run.id, idx, { takeSP, price, stake });
      setRun((prev) =>
        prev
          ? { ...prev, bets: prev.bets.map((b, i) => (i === idx ? { ...b, status: 'placed' } : b)) }
          : prev
      );
      setConfirmBetIdx(null); // success — close the modal
    } catch (e: any) {
      // Keep the modal open and show exactly why Betfair refused the bet.
      setConfirmErr(e?.message || 'Could not place this bet');
    } finally {
      setConfirming(null);
    }
  };

  const onReview = async () => {
    if (!run) return;
    setReviewing(true);
    setError(null);
    try {
      const updated = await reviewStrategyRun(run.id);
      setRun(updated);
      loadHistory();
    } catch (e: any) {
      setError(e?.message || 'Review failed');
    } finally {
      setReviewing(false);
    }
  };

  // Re-price the run's offers against the live market: refresh staged prices/edges/
  // stakes AND move any placed-but-unmatched orders to the current best price. Clears
  // manual edits (they were against the old prices) and refreshes the bank.
  const onReprice = async () => {
    if (!run) return;
    setRepricing(true);
    setError(null);
    setRepriceMsg(null);
    try {
      // v2.4 Change 9 — re-price re-slices today's bank off the live balance at the
      // current commitment %; the summary returns the live balance and committed bank.
      const { run: updated, summary } = await repriceStrategyRun(run.id, riskPct / 100);
      setRun(updated);
      setEdits({});
      const live = summary.live_balance ?? summary.bank;
      if (live != null) {
        setLiveBank(live);
        setBank((b) => ({ ...b, current: live as number }));
      }
      const parts: string[] = [];
      parts.push(`${summary.staged_repriced} offer${summary.staged_repriced === 1 ? '' : 's'} refreshed`);
      if (summary.orders_repriced > 0) parts.push(`${summary.orders_repriced} live order${summary.orders_repriced === 1 ? '' : 's'} re-priced`);
      setRepriceMsg(`Re-priced to live market — ${parts.join(' · ')}.`);
    } catch (e: any) {
      const msg = e?.message || 'Re-price failed';
      // A "Run not found" means this run id is stale — it was replaced by a re-run.
      // Self-heal: reload whatever run now exists for the day instead of dead-ending.
      if (/not found/i.test(msg)) {
        setError('This strategy was replaced by a newer run — reloading the current one.');
        try { localStorage.removeItem(LAST_RUN_KEY); } catch { /* ignore */ }
        await loadRunForDate(date);
      } else {
        setError(msg);
      }
    } finally {
      setRepricing(false);
    }
  };

  // Fit the model's overconfidence factor from settled results (v2.3 Ch1).
  const onTune = async () => {
    setTuning(true);
    setTuneMsg(null);
    try {
      const fit = await fitCalibration();
      setTuneMsg(fit.message);
    } catch (e: any) {
      setTuneMsg(e?.message || 'Calibration fit failed');
    } finally {
      setTuning(false);
    }
  };

  const fmtMoney = (n: number | null | undefined) =>
    n == null ? '—' : `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-AU', { maximumFractionDigits: 2 })}`;

  // Effective price/stake for a bet, honouring any inline manual override.
  const effPrice = (b: StagedBet, idx: number) => {
    const e = edits[idx];
    return e?.price != null && e.price !== '' ? Number(e.price) : b.price_at_decision;
  };
  const effStake = (b: StagedBet, idx: number) => {
    const e = edits[idx];
    return e?.stake != null && e.stake !== '' ? Number(e.stake) : b.stake;
  };
  const setEdit = (idx: number, field: 'price' | 'stake', value: string) =>
    setEdits((prev) => ({ ...prev, [idx]: { ...prev[idx], [field]: value } }));

  // v2.4 Change 9 — the bet slate rows, sortable three ways. Each row keeps its
  // original index so confirm/edit still address the right bet. Accumulators are
  // excluded (the slate is the single/dutch running order); unplaceable picks too.
  const slateRows = useMemo(() => {
    const rows = (run?.bets || [])
      .map((b, idx) => ({ b, idx }))
      .filter(({ b }) => b.structure !== 'accumulator' && b.status !== 'unplaceable');
    const time = (b: StagedBet) => (b.start_time ? new Date(b.start_time).getTime() : Number.POSITIVE_INFINITY);
    const priceOf = (b: StagedBet, idx: number) => Number(effPrice(b, idx)) || Number.POSITIVE_INFINITY;
    const edgeOf = (b: StagedBet) => (b.edge_pct == null ? Number.NEGATIVE_INFINITY : Number(b.edge_pct));
    const sorted = rows.slice();
    if (slateSort === 'time') {
      sorted.sort((x, y) => time(x.b) - time(y.b) || x.b.track.localeCompare(y.b.track) || x.b.race - y.b.race);
    } else if (slateSort === 'price') {
      sorted.sort((x, y) => priceOf(x.b, x.idx) - priceOf(y.b, y.idx));
    } else if (slateSort === 'track') {
      // Track A→Z, then race number ascending (the morning-review order).
      sorted.sort((x, y) => x.b.track.localeCompare(y.b.track) || x.b.race - y.b.race || time(x.b) - time(y.b));
    } else {
      sorted.sort((x, y) => edgeOf(y.b) - edgeOf(x.b)); // edge ranking: most confident first
    }
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.bets, edits, slateSort]);

  // Format a jump time (HH:MM) for the slate; '—' when the start time is unknown.
  const fmtJump = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  // Live staking summary across the still-stageable single bets (honours edits).
  // v2.4 Change 9 — the live Betfair balance and today's COMMITTED bank (the sliced
  // figure stakes are sized from = live balance × commitment %).
  const liveBalance = liveBank ?? bank.current;
  const committedBank = Math.round((liveBalance * riskPct) / 100 * 100) / 100;

  const staking = useMemo(() => {
    const bets = run?.bets || [];
    let total = 0;
    let count = 0;
    bets.forEach((b, idx) => {
      if (b.structure === 'accumulator') return;
      if (b.status === 'unplaceable') return;
      const s = effStake(b, idx);
      if (Number.isFinite(Number(s)) && Number(s) > 0) {
        total += Number(s);
        count += 1;
      }
    });
    // Total risk is read against today's committed bank (the slate header's % of bank).
    const bankNow = committedBank > 0 ? committedBank : liveBalance;
    return { total, count, pct: bankNow > 0 ? (total / bankNow) * 100 : 0, bankNow };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.bets, edits, committedBank, liveBalance]);

  // Staking-Separation Spec: has Button 2 run for this loaded run? Until it has, picks
  // are 'selected' (unstaked) and the place buttons stay disabled.
  const isStaked = run?.staking_state === 'staked';
  // Picks the staking pass can size (placeable singles) — drives the Run-staking count.
  const stakeableCount = (run?.bets || []).filter(
    (b, idx) => b.structure !== 'accumulator' && b.status !== 'unplaceable' && !deselected.has(idx)
  ).length;

  return (
    <div className="claude-strategy">
      <header className="cs-header">
        <div className="cs-header-icon">AI</div>
        <div>
          <h1>Claude Strategy</h1>
          <p className="cs-sub">
            Assemble the day's races and notes, let Claude return value bets, stage them for one-tap
            confirm, then review after racing.
          </p>
        </div>
      </header>

      <ol className="cs-steps">
        <li>
          <span className="cs-step-n">1</span>
          <span>
            <strong>Run selections</strong> — Claude reads the whole card and returns the day's picks, <em>unstaked</em>.
          </span>
        </li>
        <li>
          <span className="cs-step-n">2</span>
          <span>
            <strong>Opt out &amp; route</strong> — deselect any pick and choose its platform (Betfair / TAB / cash).
          </span>
        </li>
        <li>
          <span className="cs-step-n">3</span>
          <span>
            <strong>Run staking</strong> — sizes the survivors (65/35 · On-Base Dutched · Value ¼-Kelly). Re-runnable.
          </span>
        </li>
        <li>
          <span className="cs-step-n">4</span>
          <span>
            <strong>Confirm &amp; place</strong> a bet (needs Betfair), then <strong>Run review</strong> after racing.
          </span>
        </li>
      </ol>

      {error && (
        <div className="cs-error" role="alert">
          <span>⚠</span> {error}
        </div>
      )}

      <div className="cs-grid">
        {/* ── Left: race + bank selection ── */}
        <section className="cs-panel cs-controls">
          <div className="cs-field">
            <label htmlFor="cs-date">Race day</label>
            <input id="cs-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <fieldset className="cs-bankroll">
            <legend>Bank &amp; commitment</legend>
            {/* v2.4 Change 9 — Option B bank model. The real Betfair balance at
                generation IS the total bank; the commitment slider sets how much of
                it today's plan risks. Read-only live balance with a live/offline tag. */}
            <div className="cs-field cs-bank-field">
              <label>Live Betfair balance</label>
              <div className="cs-money readonly" title="Read-only — the real money in your Betfair account right now">
                <span>$</span>
                <input
                  type="number"
                  value={Number.isFinite(Number(liveBalance)) ? Number(liveBalance) : 0}
                  readOnly
                  tabIndex={-1}
                />
                <button
                  type="button"
                  className="cs-bank-refresh"
                  onClick={loadBank}
                  disabled={bankLoading}
                  title="Refresh balance from Betfair"
                >
                  {bankLoading ? '…' : '↻'}
                </button>
              </div>
              <span className={`cs-bank-caption${liveBank != null ? ' live' : ''}`}>
                {liveBank != null ? '● Live Betfair balance' : '○ Betfair not connected'}
              </span>
            </div>

            {/* Commitment slider: today's bank = live balance × this %. Every stake is
                computed from the sliced figure. Top up = deposit + regenerate. */}
            <div className="cs-risk">
              <div className="cs-risk-head">
                <label htmlFor="cs-risk">Commitment (% of live balance)</label>
                <span className="cs-risk-val">
                  {riskPct}%
                  {liveBalance ? ` · today's bank ${fmtMoney(committedBank)}` : ''}
                </span>
              </div>
              <input
                id="cs-risk"
                type="range"
                min={5}
                max={100}
                step={5}
                value={riskPct}
                onChange={(e) => setRiskPct(Number(e.target.value))}
                className="cs-risk-range"
              />
              <div className="cs-risk-presets">
                {[25, 50, 100].map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`cs-risk-preset${riskPct === p ? ' active' : ''}`}
                    onClick={() => setRiskPct(p)}
                  >
                    {p}%
                  </button>
                ))}
              </div>
            </div>

            {/* Staking plan: a fixed-unit staked plan, OR a flat % of the live bank
                per bet (the strategy default). Either is a valid Stage-1 plan — the
                operator picks per run. Kelly stays calibration-gated server-side. */}
            <div className="cs-field" style={{ marginTop: 12 }}>
              <label>Staking plan</label>
              <div className="cs-risk-presets" role="group" aria-label="Staking plan">
                <button
                  type="button"
                  className={`cs-risk-preset${stakeMode === 'flat_fraction' ? ' active' : ''}`}
                  onClick={() => setStakeMode('flat_fraction')}
                >
                  Fractional bank
                </button>
                <button
                  type="button"
                  className={`cs-risk-preset${stakeMode === 'flat' ? ' active' : ''}`}
                  onClick={() => setStakeMode('flat')}
                >
                  Fixed staked plan
                </button>
              </div>
              {stakeMode === 'flat' && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label htmlFor="cs-flat-unit">Stake per bet ($)</label>
                  <input
                    id="cs-flat-unit"
                    type="number"
                    min={minStake}
                    step={5}
                    value={flatUnit}
                    onChange={(e) => setFlatUnit(Math.max(minStake, Number(e.target.value) || minStake))}
                    style={{ width: 90 }}
                  />
                </div>
              )}
            </div>

            <p className="cs-hint">
              {stakeMode === 'flat'
                ? <>Staking plan: <strong>fixed {fmtMoney(flatUnit)} per bet</strong> (bank-independent).</>
                : <>Staking plan: <strong>fractional bank</strong> — a flat % of the committed bank per bet, ¼-Kelly once calibration passes.</>}
              {' '}Today's bank is <strong>{riskPct}% of the live balance</strong>
              {liveBalance ? ` = ${fmtMoney(committedBank)}` : ''}. Every stake sizes off this figure
              (flat fraction now, ¼-Kelly once calibration passes). No daily stop-loss — the day's plan
              runs end to end; total risk is bounded up front by the bank and the caps.
              To add money mid-day, deposit and <strong>regenerate</strong>. {fmtMoney(minStake)} Betfair minimum.
            </p>
          </fieldset>

          <label className="cs-toggle">
            <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} />
            <span className="cs-toggle-track" aria-hidden>
              <span className="cs-toggle-thumb" />
            </span>
            <span className="cs-toggle-label">Include my voice notes</span>
          </label>

          <label className="cs-toggle">
            <input type="checkbox" checked={metroOnly} onChange={(e) => setMetroOnly(e.target.checked)} />
            <span className="cs-toggle-track" aria-hidden>
              <span className="cs-toggle-thumb" />
            </span>
            <span className="cs-toggle-label">Metro meetings only</span>
          </label>

          <div className="cs-section-head">
            <span>{metroOnly && metroMeetings.length > 0 ? 'Metro card' : "Today's card"}</span>
            <span className="cs-count">
              {raceCount} race{raceCount === 1 ? '' : 's'} · {shownMeetings.length} meeting{shownMeetings.length === 1 ? '' : 's'}
            </span>
          </div>

          <p className="cs-hint">
            Claude assesses <strong>every meeting</strong> on the card (one pass per meeting) and
            merges them into a single strategy, ranked strongest-first — from many bets to none. No
            per-race selection. Tick <em>Metro meetings only</em> for a faster, cheaper run.
          </p>

          <div className="cs-meetings">
            {loadingMeetings ? (
              <p className="cs-muted">Loading meetings…</p>
            ) : shownMeetings.length === 0 ? (
              <p className="cs-muted">No meetings found for this date.</p>
            ) : (
              shownMeetings.map((m) => (
                <div key={m._id || m.meeting_name} className="cs-meeting">
                  <div className="cs-meeting-head">
                    <strong>{m.meeting_name}</strong>
                    <span className="cs-count">{m.races?.length ?? 0}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {run ? (
            <>
              <button
                className="cs-build"
                disabled={building || raceCount === 0}
                onClick={() => setConfirmRebuild(true)}
                title="Re-run BetBuilder — fresh selections over the whole card (internal engine, no Claude)"
              >
                {building ? (
                  <>
                    <span className="cs-spinner" /> Running BetBuilder…
                  </>
                ) : (
                  "↻ Re-run BetBuilder (replaces today's picks)"
                )}
              </button>
              <button
                className="cs-build cs-reprice"
                disabled={repricing || building}
                onClick={onReprice}
                title="Keep the same picks, refresh prices & stakes to the current live market (and move any unmatched live orders)"
              >
                {repricing ? (
                  <>
                    <span className="cs-spinner" /> Re-pricing…
                  </>
                ) : (
                  '↻ Re-price to live market'
                )}
              </button>
              {/* Staking-Separation Spec — Button 2. Stakes the post-opt-out picks
                  (65/35 split, On-Base Dutched by price, Value quarter-Kelly). Re-runnable:
                  change the bank / opt-out / platform and press again to re-stake. */}
              <button
                className="cs-build cs-stake-btn"
                disabled={stakingBusy || building || stakeableCount === 0}
                onClick={onStake}
                title="Calculate dollar stakes for the selected picks — On-Base Dutched by price, Value quarter-Kelly, capped at your committed bank. Nothing is placed."
              >
                {stakingBusy ? (
                  <>
                    <span className="cs-spinner" /> Calculating stakes…
                  </>
                ) : (
                  `${isStaked ? '↻ Re-run staking' : '② Run staking'} · ${stakeableCount} pick${stakeableCount === 1 ? '' : 's'}`
                )}
              </button>
            </>
          ) : (
            <button className="cs-build" disabled={building || raceCount === 0} onClick={() => onBuild(false)}>
              {building ? (
                <>
                  <span className="cs-spinner" /> Running BetBuilder…
                </>
              ) : (
                `① Run selections · ${raceCount} race${raceCount === 1 ? '' : 's'}`
              )}
            </button>
          )}
          {stakeErr && <p className="cs-hint cs-err" style={{ marginTop: 6 }}>{stakeErr}</p>}

          {/* v2.3 Ch1 — empirical tuning: re-fit the model's overconfidence factor
              from settled results. Safe to click anytime (identity until enough data). */}
          <button
            className="cs-tune"
            disabled={tuning}
            onClick={onTune}
            title="Re-fit the model's calibration from your settled results — the engine shrinks over-rated probabilities by the fitted factor on the next build"
          >
            {tuning ? 'Tuning…' : '🎯 Tune from results'}
          </button>
          {tuneMsg && <p className="cs-hint" style={{ marginTop: 6 }}>{tuneMsg}</p>}
        </section>

        {/* ── Right: staged card / review ── */}
        <section className="cs-panel cs-result">
          {!run ? (
            <div className="cs-empty">
              <div className="cs-empty-mark">🏇</div>
              <h3>No strategy yet</h3>
              <p>
                On the left: pick a race day, then press <strong>① Run selections</strong>. Claude reads the
                whole card once and the picks appear here — unstaked. Opt out any pick, route it to a platform,
                then press <strong>② Run staking</strong> to size them.
              </p>
              <p className="cs-empty-tip">
                Tip: most small fields are correctly a <em>no-bet day</em> — you can still discuss any race with Claude.
              </p>
            </div>
          ) : (
            <>
              <div className="cs-brief">
                <div className="cs-brief-head">
                  <span className={`cs-status cs-${run.status}`}>{run.status.replace('_', ' ')}</span>
                  <div className="cs-brief-head-right">
                    <span className="cs-brief-date">{run.date}</span>
                    <button className="cs-close" onClick={closeRun} title="Close this strategy (it stays saved in Recent runs)">
                      ✕ Close
                    </button>
                  </div>
                </div>
                <p className="cs-brief-text">{renderRich(run.day_brief)}</p>
                {run.market_read && (
                  <div className="cs-market-read">
                    <span className="cs-mini-label">Market read</span>
                    <p>{renderRich(run.market_read)}</p>
                  </div>
                )}
                {run.key_themes && run.key_themes.length > 0 && (
                  <ul className="cs-themes">
                    {run.key_themes.map((t, i) => (
                      <li key={i}>{renderRich(t)}</li>
                    ))}
                  </ul>
                )}
                {run.notes_feedback && (
                  <p className="cs-notes-fb">
                    <strong>On your notes:</strong> {run.notes_feedback}
                  </p>
                )}
              </div>

              {/* Top-level staking summary — live bank, total staged, % of bank.
                  Updates as prices/stakes are re-priced or edited inline. */}
              <div className="cs-staking-bar">
                <div className="cs-stk-cell">
                  <span className="cs-stk-lbl">Bank (live)</span>
                  <strong className="cs-stk-val">{fmtMoney(staking.bankNow)}</strong>
                </div>
                <div className="cs-stk-cell">
                  <span className="cs-stk-lbl">Staking</span>
                  <strong className="cs-stk-val">{fmtMoney(staking.total)}</strong>
                </div>
                <div className="cs-stk-cell">
                  <span className="cs-stk-lbl">Bets</span>
                  <strong className="cs-stk-val">{staking.count}</strong>
                </div>
                <div className="cs-stk-cell">
                  <span className="cs-stk-lbl">% of bank</span>
                  <strong className={`cs-stk-val${staking.pct > 20 ? ' over' : ''}`}>
                    {staking.pct.toFixed(1)}%
                  </strong>
                </div>
                <button className="cs-stk-reprice" onClick={onReprice} disabled={repricing || building}>
                  {repricing ? 'Re-pricing…' : '↻ Re-price to live market'}
                </button>
              </div>
              {repriceMsg && (
                <div className="cs-reprice-msg" role="status">{repriceMsg}</div>
              )}

              {/* ── Staking-Separation Spec — selection vs staking state ───────────
                  Until Button 2 runs, the card is a SELECTED (unstaked) pick list. */}
              {!isStaked ? (
                <div className="cs-reprice-msg" role="status" style={{ background: '#fff7e6', color: '#8a5a00' }}>
                  ① Selections only — picks are <strong>not yet staked</strong>. Opt out any pick and set its
                  platform below, then press <strong>Run staking</strong> to size them (65/35 split · On-Base
                  Dutched by price · Value quarter-Kelly). Placing is disabled until then.
                </div>
              ) : run.staking_summary ? (
                <div className="cs-stake-summary" role="status">
                  <div className="cs-stk-cell">
                    <span className="cs-stk-lbl">On-Base pool (Dutch)</span>
                    <strong className="cs-stk-val">{fmtMoney(run.staking_summary.onbase_pool)}</strong>
                  </div>
                  <div className="cs-stk-cell">
                    <span className="cs-stk-lbl">Value pool (¼-Kelly)</span>
                    <strong className="cs-stk-val">{fmtMoney(run.staking_summary.value_pool)}</strong>
                  </div>
                  <div className="cs-stk-cell">
                    <span className="cs-stk-lbl">Committed / available</span>
                    <strong className="cs-stk-val">
                      {fmtMoney(run.staking_summary.committed)} / {fmtMoney(run.staking_summary.available)}
                    </strong>
                  </div>
                  <div className="cs-stk-cell">
                    <span className="cs-stk-lbl">Staked · not-staked</span>
                    <strong className="cs-stk-val">
                      {run.staking_summary.staked_count} · {run.staking_summary.selected_not_staked_count}
                    </strong>
                  </div>
                  {run.staking_summary.scaled && (
                    <span className="cs-pill warn" title="Stakes scaled down proportionally so committed never exceeds the available bank (65/35 shape kept).">
                      scaled to bank
                    </span>
                  )}
                </div>
              ) : null}

              {/* ── v2.4 Change 9 — BET SLATE ───────────────────────────────────
                  Edge, price and dollar stake on one row per bet, with a WIN/PLACE
                  tag; total committed + count up front; sortable by time, price, edge. */}
              {slateRows.length > 0 && (
                <div className="cs-slate">
                  <div className="cs-slate-head">
                    <button
                      type="button"
                      className="cs-slate-toggle"
                      onClick={() => setShowSlate((v) => !v)}
                      title="Show / hide the bet slate"
                    >
                      📋 <strong>Bet slate</strong>
                      <span className="cs-slate-summary">
                        {staking.count} bet{staking.count === 1 ? '' : 's'} · {fmtMoney(staking.total)} of {fmtMoney(staking.bankNow)} committed
                      </span>
                      <span className="cs-slate-chev">{showSlate ? '▴' : '▾'}</span>
                    </button>
                    {showSlate && (
                      <div className="cs-slate-sort" role="tablist" aria-label="Sort the slate">
                        <span className="cs-slate-sort-lbl">Sort:</span>
                        {([['time', 'Jump time'], ['track', 'Track'], ['price', 'Price'], ['edge', 'Edge']] as const).map(([k, lbl]) => (
                          <button
                            key={k}
                            type="button"
                            className={`cs-slate-sort-btn${slateSort === k ? ' active' : ''}`}
                            onClick={() => setSlateSort(k)}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {showSlate && (
                    <div className="cs-slate-table" role="table">
                      <div className="cs-slate-row cs-slate-row-head" role="row">
                        <span className="cs-slate-c-type">Type</span>
                        <span className="cs-slate-c-runner">Horse</span>
                        <span className="cs-slate-c-race">Race · jump</span>
                        <span className="cs-slate-c-price">Price</span>
                        <span className="cs-slate-c-edge">Edge</span>
                        <span className="cs-slate-c-state">State</span>
                        <span className="cs-slate-c-stake">Stake</span>
                      </div>
                      {slateRows.map(({ b, idx }) => {
                        const isPlace = (b.market_type || 'WIN') === 'PLACE';
                        const ep = effPrice(b, idx);
                        const es = effStake(b, idx);
                        return (
                          <div key={idx} className={`cs-slate-row${b.status === 'placed' ? ' placed' : ''}${b.no_value ? ' no-value' : ''}`} role="row">
                            <span className="cs-slate-c-type">
                              <span className={`cs-tag ${isPlace ? 'place' : 'win'}`}>{isPlace ? 'PLACE' : 'WIN'}</span>
                              {b.side === 'lay' && <span className="cs-tag lay">LAY</span>}
                            </span>
                            <span className="cs-slate-c-runner" title={b.runner}>{b.runner}</span>
                            <span className="cs-slate-c-race">{b.track} R{b.race} · {fmtJump(b.start_time)}</span>
                            <span className="cs-slate-c-price">{ep != null ? `$${Number(ep).toFixed(2)}` : '—'}</span>
                            <span className={`cs-slate-c-edge${b.edge_pct != null && b.edge_pct < 0 ? ' neg' : ''}`}>
                              {b.edge_pct != null ? `${b.edge_pct > 0 ? '+' : ''}${b.edge_pct}%` : '—'}
                            </span>
                            <span className="cs-slate-c-state">
                              {b.race_state ? (
                                <span className={`cs-state s${b.race_state}`} title={b.race_state_label || ''}>
                                  {b.race_state === 1 ? 'False-fav' : b.race_state === 2 ? 'Fair-fav' : b.race_state === 4 ? 'False-top3' : `S${b.race_state}`}
                                </span>
                              ) : '—'}
                            </span>
                            <span
                              className="cs-slate-c-stake"
                              title={committedBank > 0 ? `${((Number(es) / committedBank) * 100).toFixed(1)}% of today's committed bank (${fmtMoney(committedBank)})` : undefined}
                            >{fmtMoney(Number(es))}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Day-level conversation — its own prominent bar so it's never missed. */}
              {(() => {
                const t = findThread('day', {});
                const open = openSections.has('day');
                const count = t ? Math.ceil(t.messages.length / 2) : 0;
                return (
                  <div className={`cs-discuss-bar${open ? ' open' : ''}`}>
                    <button className="cs-discuss-bar-btn" onClick={() => toggleSection('day')}>
                      <span className="cs-discuss-bar-left">
                        💬 <strong>Discuss the day with Claude</strong>
                        {count > 0 && <span className="cs-discuss-count">{count}</span>}
                      </span>
                      <span className="cs-discuss-bar-chev">{open ? 'Hide ▴' : 'Open ▾'}</span>
                    </button>
                    {open && (
                      <SectionChat
                        runId={run.id}
                        scope="day"
                        refObj={{}}
                        initial={t}
                        voice={{ scope: 'session', context: { sessionId: `run_${run.id}` } }}
                        onUpdated={onThreadUpdated}
                      />
                    )}
                  </div>
                );
              })()}

              {(() => {
                const engine = run.engine;
                if (!engine) return null;
                return (
                  <div
                    className="cs-engine"
                    style={{ border: '1px solid #d8dee9', borderRadius: 10, padding: '12px 14px', margin: '12px 0', background: '#f5f7fc', color: '#1f2733' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: 0.5, background: '#3b5bdb', color: '#fff', padding: '2px 8px', borderRadius: 6 }}>
                        {engine.version.toUpperCase()} MODEL
                      </span>
                      <strong style={{ fontSize: 14 }}>{engine.coverage.overall}% data coverage</strong>
                      {engine.calibration?.beta != null && (
                        <span
                          style={{ fontSize: 11, fontWeight: 600, background: '#e6f4ea', color: '#2b8a3e', padding: '2px 7px', borderRadius: 6, border: '1px solid #a3d9b1' }}
                          title={engine.calibration._meta?.test_top1 ? `Calibrated logit; held-out top-1 ${engine.calibration._meta.test_top1}% vs ~16% random` : 'Calibrated logit temperature'}
                        >
                          calibrated β={engine.calibration.beta}
                        </span>
                      )}
                      <span style={{ opacity: 0.65, fontSize: 12, color: '#55607a' }}>· going-adjusted-margins rating, runs alongside Claude as a cross-check</span>
                    </div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      {Object.keys(engine.factor_weights).map((f) => {
                        const pct = engine.coverage.by_factor[f] ?? 0;
                        return (
                          <div
                            key={f}
                            style={{ display: 'grid', gridTemplateColumns: '190px 1fr 40px', alignItems: 'center', gap: 8, fontSize: 12 }}
                            title={`factor weight ${engine.factor_weights[f]}`}
                          >
                            <span style={{ opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {engine.factor_names[f]} <span style={{ opacity: 0.5 }}>({engine.factor_weights[f]})</span>
                            </span>
                            <span style={{ height: 7, background: '#dde3ee', borderRadius: 4, overflow: 'hidden' }}>
                              <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: pct >= 66 ? '#37b24d' : pct >= 33 ? '#f59f00' : '#e8590c' }} />
                            </span>
                            <span style={{ textAlign: 'right', opacity: 0.8 }}>{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {run.bets.length > 0 && (
                <div className="cs-bets">
                  {run.bets.map((b: StagedBet, idx: number) => (
                    <div key={idx} className={`cs-bet ${b.side}`}>
                      <div className="cs-bet-top">
                        <div className="cs-bet-id">
                          <span className={`cs-bet-side ${b.side}`}>{b.side}</span>
                          <strong className="cs-bet-runner">{b.runner}</strong>
                          <span className="cs-bet-race">
                            {b.track} · R{b.race}
                          </span>
                        </div>
                        {/* Client, 2026-08-14: now betting these manually off this page rather than
                            through the bot, so it needs to say which picks are in the currently-
                            approved weekend bucket allow-list. null = simple gating is off (no
                            allow-list in force right now), so no badge either way. */}
                        {b.bucket_live_approved != null && (
                          <span
                            className="cs-pill"
                            title={b.bucket_live_approved
                              ? 'This bucket is in the currently-approved weekend live-gating allow-list.'
                              : 'This bucket is not on the current live allow-list — still measuring on paper, not yet trusted for real money.'}
                            style={b.bucket_live_approved
                              ? { borderColor: '#2b8a3e', color: '#2b8a3e', background: '#ebfbee', fontWeight: 700 }
                              : { borderColor: '#adb5bd', color: '#495057', background: '#f1f3f5', fontWeight: 700 }}
                          >
                            {b.bucket_live_approved ? '✓ live-approved bucket' : 'not yet approved'}
                          </span>
                        )}
                        <span className={`cs-conf ${confidenceClass(b.confidence)}`}>{b.confidence || '—'}</span>
                      </div>

                      {b.key_signal && (
                        <div className="cs-key-signal">
                          <span className="cs-key-icon">★</span> {renderRich(b.key_signal)}
                        </div>
                      )}

                      <div className="cs-bet-meta">
                        {b.status === 'staged' && b.structure !== 'accumulator' ? (
                          <>
                            {/* Manual price & stake — editable before placing. Empty = use staged. */}
                            <span className="cs-edit-pill" title="Price to take (override before placing)">
                              @
                              <input
                                type="number"
                                step="0.01"
                                min={1.01}
                                value={edits[idx]?.price ?? (b.price_at_decision ?? '')}
                                onChange={(e) => setEdit(idx, 'price', e.target.value)}
                              />
                            </span>
                            <span className="cs-edit-pill" title={`Stake (Betfair minimum ${fmtMoney(minStake)})`}>
                              $
                              <input
                                type="number"
                                step="1"
                                min={minStake}
                                value={edits[idx]?.stake ?? (b.stake ?? '')}
                                onChange={(e) => setEdit(idx, 'stake', e.target.value)}
                              />
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="cs-pill">@ {b.price_at_decision ?? '—'}</span>
                            <span className="cs-pill">{fmtMoney(b.placed_stake ?? b.stake)}</span>
                          </>
                        )}
                        <span className="cs-pill ghost">{b.structure}</span>
                        {(b.engine_source === 'on_base' || b.engine_source === 'both') && (
                          <span
                            className="cs-pill"
                            title={`On-Base dispersion standout${b.engine_source === 'both' ? ' (value engine also backs it)' : ''}${b.conviction != null ? ` · conviction ${b.conviction}%` : ''}${b.stake_multiplier && b.stake_multiplier > 1 ? ` · staked ×${b.stake_multiplier}` : ''}`}
                            style={{ borderColor: '#7048e8', color: '#6741d9', background: '#f3f0ff', fontWeight: 700 }}
                          >
                            {/* There are several On-Base buckets (S1/S2 × Genuine/Bootstrap) — the
                                exact one this pick landed in, not just the engine name (client,
                                2026-08-14). Falls back to the old generic text if a legacy run
                                somehow has no bucket_label. */}
                            ◎ {b.bucket_label || 'on-base'}
                            {b.conviction != null ? ` · ${b.conviction}%` : ''}
                          </span>
                        )}
                        {b.onbase_floor_fill && (
                          <span
                            className="cs-pill"
                            title="Daily-minimum fill — no race cleared the standout bar today, so the strongest available standout was taken to meet the On-Base floor. Lower confidence than a clean standout."
                            style={{ borderColor: '#f08c00', color: '#e8590c', background: '#fff4e6', fontWeight: 700 }}
                          >
                            ◎ floor fill
                          </span>
                        )}
                        {/* Engine label for value-family picks (On-Base/fair-fav have their own
                            pills above). Every pick now shows which engine owns it. */}
                        {b.engine_source === 'value' && (
                          <span
                            className="cs-pill"
                            title={b.engine_label === 'FALSE_FAV'
                              ? 'Value pick in a State-1 false-favourite race — the market favourite is vulnerable and this runner is a priced-up overlay (a multi-bet leg if 2–3 overlays qualify).'
                              : 'Value overlay — backed because the offered price is longer than the model’s fair price (price-led engine).'}
                            style={b.engine_label === 'FALSE_FAV'
                              ? { borderColor: '#f08c00', color: '#e8590c', background: '#fff4e6', fontWeight: 700 }
                              : { borderColor: '#4263eb', color: '#3b5bdb', background: '#edf2ff', fontWeight: 700 }}
                          >
                            ◆ {b.engine_label === 'FALSE_FAV' ? 'false-fav' : 'value'}
                          </span>
                        )}
                        {b.engine_source === 'fair_fav' && (
                          <span
                            className="cs-pill"
                            title="Favourite assessment State 2 — the market favourite is fairly priced, so it's backed as a fair winner (no overlay required). The winning favourites the overlay-only engine used to skip."
                            style={{ borderColor: '#1971c2', color: '#1864ab', background: '#e7f5ff', fontWeight: 700 }}
                          >
                            ★ fair favourite
                          </span>
                        )}
                        {b.intent_score != null && b.intent_signals && b.intent_signals.length > 0 && (
                          <span className="cs-pill" title={`Trainer intent ${b.intent_score} — ${b.intent_signals.join(', ')}`}
                            style={{ borderColor: '#0ca678', color: '#099268', background: '#e6fcf5', fontWeight: 600 }}>
                            intent {b.intent_score}
                          </span>
                        )}
                        {b.reliability != null && (b.engine_source === 'on_base' || b.engine_source === 'both') && (
                          <span className="cs-pill" title="Ch3 race reliability — model win-prob is shrunk toward the market below 100%"
                            style={{ borderColor: '#f08c00', color: '#e8590c', background: '#fff4e6', fontWeight: 600 }}>
                            rel {b.reliability}%
                          </span>
                        )}
                        {b.late_money_pts != null && b.late_money_pts !== 0 && (
                          <span className="cs-pill" title="Ch6 late-money read (minute-level): + = firming/supported, − = drifting"
                            style={b.late_money_pts > 0
                              ? { borderColor: '#2b8a3e', color: '#2b8a3e', background: '#ebfbee', fontWeight: 600 }
                              : { borderColor: '#e03131', color: '#c92a2a', background: '#fff5f5', fontWeight: 600 }}>
                            {b.late_money_pts > 0 ? '↘ firm' : '↗ drift'} {Math.abs(b.late_money_pts)}pt
                          </span>
                        )}
                        {b.no_value && (
                          <span className="cs-pill warn" title="At the current live price the edge is gone (Kelly stake 0). Place only if you still want it.">
                            ⚠ no value now
                          </span>
                        )}
                        {b.execution_window && b.execution_window !== 'default' && (
                          <span
                            className="cs-pill"
                            title={b.execution_window === 'early'
                              ? 'Firm-candidate — work it EARLY to be the early money'
                              : 'Value/longshot — work it LATE, into the deepest market'}
                            style={{ borderColor: '#5c7cfa', color: '#3b5bdb', background: '#edf2ff', fontWeight: 600 }}
                          >
                            {b.execution_window === 'early' ? '⏱ work early' : '⏱ work late'}
                          </span>
                        )}
                        {b.engine_rank != null && (
                          <span
                            className="cs-pill"
                            title={b.engine_rank === 1 ? 'v2.2 model agrees — its top-rated runner' : `v2.2 model rates this #${b.engine_rank}`}
                            style={{
                              borderColor: b.engine_rank === 1 ? '#37b24d' : '#f59f00',
                              color: b.engine_rank === 1 ? '#2b8a3e' : '#b45309',
                              background: b.engine_rank === 1 ? '#e9f7ee' : '#fff4e6',
                              fontWeight: 600,
                            }}
                          >
                            {b.engine_rank === 1 ? '✓ ' : ''}model #{b.engine_rank} @ {b.engine_fair_price ?? '—'}
                            {b.engine_win_pct != null ? ` · ${b.engine_win_pct}%` : ''}
                          </span>
                        )}
                        {b.structure === 'dutch' && (
                          <span className={`cs-pill ${b.dutch_profitable ? 'good' : 'warn'}`}>
                            book {b.dutch_profitable ? 'profits' : `over ${b.dutch_overround}`}
                          </span>
                        )}
                        {['win', 'lose', 'void'].includes(b.result || '') && (
                          <span className={`cs-pill result ${b.result}`}>{b.result}</span>
                        )}
                        {b.result === 'no_bet' && <span className="cs-pill ghost">not backed</span>}
                        {b.clv != null && <span className="cs-pill clv">CLV {b.clv}%</span>}
                      </div>

                      {b.fair_price != null && b.price_at_decision != null && (
                        <div className="cs-edge">
                          <div className="cs-edge-cell">
                            <span className="cs-edge-num">{b.fair_price}</span>
                            <span className="cs-edge-lbl">fair{b.fair_prob != null ? ` · ${b.fair_prob}%` : ''}</span>
                          </div>
                          <span className="cs-edge-arrow">→</span>
                          <div className="cs-edge-cell">
                            <span className="cs-edge-num">{b.price_at_decision}</span>
                            <span className="cs-edge-lbl">taking{b.offered_prob != null ? ` · ${b.offered_prob}%` : ''}</span>
                          </div>
                          {b.edge_pct != null && (
                            <span className={`cs-edge-tag ${b.edge_pct >= 0 ? 'pos' : 'neg'}`}>
                              {b.edge_pct >= 0 ? '+' : ''}
                              {b.edge_pct}% edge
                            </span>
                          )}
                        </div>
                      )}

                      {(b.decision_logic || (b.gate_checks || []).length > 0 || (b.reasons || []).length > 0 || (b.risks || []).length > 0) && (
                        <>
                          <button className="cs-why" onClick={() => toggleDetails(idx)}>
                            {openDetails.has(idx) ? '▾ Hide decision logic' : '▸ Why this pick'}
                          </button>
                          {openDetails.has(idx) && (
                            <div className="cs-why-body">
                              {b.decision_logic && <p className="cs-logic">{renderRich(b.decision_logic)}</p>}

                              {(b.gate_checks || []).length > 0 && (
                                <div className="cs-gates">
                                  {(b.gate_checks || []).map((g, i) => (
                                    <div key={i} className={`cs-gate ${g.status}`} title={g.note}>
                                      <span className="cs-gate-mark">
                                        {g.status === 'pass' ? '✓' : g.status === 'fail' ? '✕' : '–'}
                                      </span>
                                      <span className="cs-gate-name">{g.gate}</span>
                                      {g.note && <span className="cs-gate-note">{g.note}</span>}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {(b.reasons || []).length > 0 && (
                                <div className="cs-why-block">
                                  <span className="cs-mini-label">Supporting factors</span>
                                  <ul className="cs-reasons">
                                    {(b.reasons || []).map((r, i) => (
                                      <li key={i}>{renderRich(r)}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {(b.risks || []).length > 0 && (
                                <div className="cs-why-block">
                                  <span className="cs-mini-label warn">Risks</span>
                                  <ul className="cs-risks">
                                    {(b.risks || []).map((r, i) => (
                                      <li key={i}>{renderRich(r)}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {b.confidence_rationale && (
                                <p className="cs-conf-rationale">
                                  <strong>Confidence ({b.confidence}):</strong> {b.confidence_rationale}
                                </p>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* Staking-Separation Spec — opt-out + per-pick platform routing.
                          Available on placeable singles; takes effect on the next Run staking. */}
                      {b.status !== 'placed' && b.status !== 'unplaceable' && b.structure !== 'accumulator' && (
                        <div className="cs-route-row">
                          <label className="cs-optout" title="Opt this pick out — the next Run staking sizes only the picks left in.">
                            <input
                              type="checkbox"
                              checked={!deselected.has(idx)}
                              onChange={() => toggleDeselect(idx)}
                            />
                            {deselected.has(idx) ? 'Opted out' : 'Include'}
                          </label>
                          <label className="cs-platform" title="Platform to stake against — the staking pass sizes on THIS book's price (TAB fixed is usually shorter than Betfair).">
                            Platform:
                            <select
                              value={routes[idx] || b.platform || 'BETFAIR'}
                              onChange={(e) => setRoute(idx, e.target.value as 'BETFAIR' | 'TAB' | 'CASH')}
                            >
                              <option value="BETFAIR">Betfair</option>
                              <option value="TAB">TAB</option>
                              <option value="CASH">Cash</option>
                            </select>
                          </label>
                          {b.staking_pool && (
                            <span className="cs-pill" title="Which staking pool sized this pick.">
                              {b.staking_pool === 'onbase' ? 'On-Base · Dutch' : 'Value · ¼-Kelly'}
                            </span>
                          )}
                        </div>
                      )}
                      {b.status === 'selected_not_staked' && b.staking_note && (
                        <div className="cs-reprice-msg" role="status" style={{ background: '#fff0f0', color: '#9a2a2a', marginTop: 6 }}>
                          Selected, not staked — {b.staking_note}
                        </div>
                      )}

                      <div className="cs-bet-actions">
                        {b.status === 'placed' ? (
                          <span className="cs-placed">✓ Placed</span>
                        ) : b.status === 'unplaceable' ? (
                          <span className="cs-unplaceable">No Betfair market</span>
                        ) : b.structure === 'accumulator' ? (
                          <span className="cs-unplaceable">Manual (accumulator)</span>
                        ) : !isStaked || b.status === 'selected' ? (
                          <span className="cs-unplaceable" title="Run staking (Button 2) to size this pick before placing.">
                            ① Selected — run staking
                          </span>
                        ) : b.status === 'selected_not_staked' ? (
                          <span className="cs-unplaceable">Selected, not staked</span>
                        ) : (
                          <button
                            className="cs-confirm"
                            disabled={confirming === idx}
                            onClick={() => askConfirm(idx)}
                          >
                            {confirming === idx ? 'Placing…' : 'Confirm & place'}
                          </button>
                        )}
                        {(() => {
                          const t = findThread('bet', { betIndex: idx });
                          const count = t?.messages.length ?? 0;
                          return (
                            <button className="cs-discuss sm" onClick={() => toggleSection(`bet:${idx}`)}>
                              💬 Discuss
                              {count > 0 && <span className="cs-discuss-count">{Math.ceil(count / 2)}</span>}
                            </button>
                          );
                        })()}
                      </div>
                      {openSections.has(`bet:${idx}`) && (
                        <SectionChat
                          runId={run.id}
                          scope="bet"
                          refObj={{ betIndex: idx }}
                          initial={findThread('bet', { betIndex: idx })}
                          voice={{
                            scope: 'horse',
                            context: {
                              horseName: b.runner,
                              meetingName: b.track,
                              raceNumber: b.race,
                              date: run.date,
                            },
                          }}
                          onUpdated={onThreadUpdated}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {run.bets.length === 0 && run.status !== 'stop_loss' && (
                <div className="cs-nobets">
                  <strong>No value bets today — and that's a correct outcome.</strong>
                  <p>
                    Nothing offered a genuine overlay inside the rules. See each race's reasoning below, or
                    open <strong>💬 Discuss</strong> on the day or a race to dig in with Claude.
                  </p>
                </div>
              )}

              {run.races.length > 0 && (
                <div className="cs-races-considered">
                  <h4>Races considered</h4>
                  <div className="cs-rc-list">
                    {run.races
                      .slice()
                      .sort((a, b) =>
                        a.meeting_name === b.meeting_name
                          ? a.race_number - b.race_number
                          : a.meeting_name.localeCompare(b.meeting_name)
                      )
                      .map((rc) => {
                        const norm = (s: string) => s.trim().toLowerCase();
                        const picks = run.bets.filter(
                          (b) => norm(b.track) === norm(rc.meeting_name) && b.race === rc.race_number
                        );
                        const noBet = run.no_bet_races.find(
                          (n) => norm(n.track) === norm(rc.meeting_name) && n.race === rc.race_number
                        );
                        const key = `race:${rc.meeting_name}#${rc.race_number}`;
                        const ref: ChatRef = { track: rc.meeting_name, race: rc.race_number };
                        const t = findThread('race', ref);
                        const count = t?.messages.length ?? 0;
                        const open = openSections.has(key);
                        const engRace = run.engine?.races.find(
                          (e) => norm(e.track) === norm(rc.meeting_name) && e.race === rc.race_number
                        );
                        const engTop = engRace?.top?.[0];
                        return (
                          <div key={key} className={`cs-rc${open ? ' open' : ''}`}>
                            <div className="cs-rc-head">
                              <div className="cs-rc-id">
                                <strong>
                                  {rc.meeting_name} R{rc.race_number}
                                </strong>
                                <span className="cs-rc-field">{rc.field_size} runners</span>
                              </div>
                              <div className="cs-rc-right">
                                {picks.length > 0 ? (
                                  <span className="cs-rc-tag pick">
                                    {picks.length} pick{picks.length > 1 ? 's' : ''}
                                  </span>
                                ) : noBet ? (
                                  <span className="cs-rc-tag nobet">No bet</span>
                                ) : (
                                  <span className="cs-rc-tag">—</span>
                                )}
                                <button className="cs-discuss sm" onClick={() => toggleSection(key)}>
                                  💬 {open ? 'Hide' : 'Discuss'}
                                  {count > 0 && <span className="cs-discuss-count">{Math.ceil(count / 2)}</span>}
                                </button>
                              </div>
                            </div>
                            {engTop && (
                              <div className="cs-rc-model" style={{ fontSize: 12, margin: '2px 0 6px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ color: '#3b5bdb', fontWeight: 700 }}>v2.2 model:</span>
                                <span style={{ color: '#1f2733' }}>{engTop.runner} @ {engTop.fair_price ?? '—'} · {engTop.win_pct}%</span>
                                {picks.length === 0 ? (
                                  <span style={{ color: '#55607a' }}>(Claude: no bet)</span>
                                ) : picks.some((b) => norm(b.runner) === norm(engTop.runner)) ? (
                                  <span style={{ color: '#2b8a3e', fontWeight: 600 }}>✓ matches Claude</span>
                                ) : (
                                  <span style={{ color: '#c2410c', fontWeight: 600 }}>≠ Claude's pick</span>
                                )}
                              </div>
                            )}
                            {noBet && (
                              <div className="cs-rc-reason">
                                <p>{renderRich(noBet.reason)}</p>
                                {noBet.what_would_change_it && (
                                  <p className="cs-rc-change">
                                    <strong>Would change it:</strong> {noBet.what_would_change_it}
                                    {noBet.closest_runner ? ` (${noBet.closest_runner})` : ''}
                                  </p>
                                )}
                              </div>
                            )}
                            {open && (
                              <SectionChat
                                runId={run.id}
                                scope="race"
                                refObj={ref}
                                initial={t}
                                voice={{
                                  scope: 'race',
                                  context: {
                                    meetingName: rc.meeting_name,
                                    raceNumber: rc.race_number,
                                    date: run.date,
                                  },
                                }}
                                onUpdated={onThreadUpdated}
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {run.review ? (
                <div className="cs-review">
                  <h3>Post-racing review</h3>
                  <p className="cs-review-text">{renderRich(run.review.review)}</p>
                  {(() => {
                    const m = run.review.metrics || {};
                    const placed = Number(m.placed ?? 0);
                    const settled = Number(m.settled ?? 0);
                    const wins = Number(m.wins ?? 0);
                    // No placed/settled bets → 0/0/0 is meaningless. Say so plainly.
                    if (settled === 0) {
                      return (
                        <div className="cs-no-metrics">
                          <strong>No settled bets to measure.</strong>
                          <p>
                            {placed === 0
                              ? 'Nothing was confirmed & placed on this day, so there is no P&L, strike rate or CLV — a no-bet day is a correct, neutral outcome (not a loss).'
                              : 'Placed bets haven’t settled yet. P&L, strike rate and CLV appear once Betfair settles the races.'}
                          </p>
                        </div>
                      );
                    }
                    return (
                      <div className="cs-metrics">
                        <div className={`cs-metric ${(m.pnl ?? 0) >= 0 ? 'pos' : 'neg'}`}>
                          <span className="cs-metric-val">{fmtMoney(m.pnl ?? 0)}</span>
                          <span className="cs-metric-label">P&amp;L</span>
                          <span className="cs-metric-sub">from {placed} placed</span>
                        </div>
                        <div className={`cs-metric ${(m.roi ?? 0) >= 0 ? 'pos' : 'neg'}`}>
                          <span className="cs-metric-val">{m.roi ?? 0}%</span>
                          <span className="cs-metric-label">ROI</span>
                          <span className="cs-metric-sub">on {fmtMoney(m.turnover ?? 0)} staked</span>
                        </div>
                        <div className="cs-metric">
                          <span className="cs-metric-val">{m.strike_rate ?? 0}%</span>
                          <span className="cs-metric-label">Strike rate</span>
                          <span className="cs-metric-sub">{wins}/{settled} won</span>
                        </div>
                        <div className={`cs-metric ${(m.clv_avg ?? 0) >= 0 ? 'pos' : 'neg'}`}>
                          <span className="cs-metric-val">{m.clv_avg ?? 0}%</span>
                          <span className="cs-metric-label">Avg CLV</span>
                          <span className="cs-metric-sub">vs Betfair SP</span>
                        </div>
                      </div>
                    );
                  })()}
                  {run.review.lessons && run.review.lessons.length > 0 && (
                    <ul className="cs-lessons">
                      {run.review.lessons.map((l, i) => (
                        <li key={i}>{l}</li>
                      ))}
                    </ul>
                  )}
                  <button className="cs-rereview" disabled={reviewing} onClick={onReview}>
                    {reviewing ? (
                      <>
                        <span className="cs-spinner" /> Re-running…
                      </>
                    ) : (
                      '↻ Re-run review'
                    )}
                  </button>
                </div>
              ) : (
                run.status !== 'stop_loss' && (
                  <div className="cs-review-cta">
                    <button className="cs-review-btn" disabled={reviewing} onClick={onReview}>
                      {reviewing ? (
                        <>
                          <span className="cs-spinner" /> Reviewing…
                        </>
                      ) : (
                        'Run post-racing review'
                      )}
                    </button>
                    <p className="cs-review-hint">
                      Run this <strong>after the races have finished</strong> — Claude grades the day, computes
                      P&amp;L / strike-rate / CLV, and weighs anything you discussed. (Results &amp; CLV fill in once
                      Betfair settles the markets.)
                    </p>
                  </div>
                )
              )}
            </>
          )}
        </section>
      </div>

      {history.length > 0 && (
        <section className="cs-history">
          <div className="cs-history-head">
            <h3>Recent runs</h3>
            <span className="cs-history-hint">Click a row to reopen{opening ? ' · loading…' : ''}</span>
          </div>
          <p className="cs-history-legend">
            P&amp;L and Avg CLV are filled in by the <strong>post-racing review</strong> — staged runs show
            <em> Pending</em> until then.
          </p>
          <div className="cs-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Bets</th>
                  <th>P&amp;L</th>
                  <th>Avg CLV</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const reviewed = !!h.review && h.status === 'reviewed';
                  const pnl = h.review?.metrics?.pnl;
                  const clv = h.review?.metrics?.clv_avg;
                  // A reviewed run that placed nothing has no realised P&L/CLV — show
                  // "—" rather than a misleading $0 / 0%.
                  const nothingPlaced = reviewed && Number(h.review?.metrics?.settled ?? 0) === 0;
                  return (
                    <tr
                      key={h.id}
                      className={`cs-history-row${run?.id === h.id ? ' active' : ''}`}
                      onClick={() => openRun(h.id)}
                      title="Reopen this run"
                    >
                      <td>{h.date}</td>
                      <td>
                        <span className={`cs-status cs-${h.status}`}>{h.status.replace('_', ' ')}</span>
                      </td>
                      <td>{h.bets.length}</td>
                      <td className={!nothingPlaced && reviewed && pnl != null ? (pnl >= 0 ? 'pos' : 'neg') : ''}>
                        {nothingPlaced ? (
                          <span className="cs-nobet-pill" title="No bets were placed — a disciplined no-action day, not a loss">
                            <span className="cs-nobet-dot" /> No bets
                          </span>
                        ) : reviewed && pnl != null ? (
                          fmtMoney(pnl)
                        ) : (
                          <span className="cs-pending">Pending</span>
                        )}
                      </td>
                      <td>
                        {nothingPlaced ? (
                          <span className="cs-nobet-pill subtle" title="No closing-line value to measure without a placed bet">
                            n/a
                          </span>
                        ) : reviewed && clv != null ? (
                          `${clv}%`
                        ) : (
                          <span className="cs-pending">Pending</span>
                        )}
                      </td>
                      <td className="cs-history-open">{run?.id === h.id ? 'Open' : 'Reopen ›'}</td>
                      <td className="cs-history-del">
                        <button
                          className="cs-del-btn"
                          title="Delete this run"
                          aria-label="Delete this run"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(h.id);
                          }}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {confirmBetIdx != null && run && run.bets[confirmBetIdx] && (() => {
        const b = run.bets[confirmBetIdx];
        const placing = confirming === confirmBetIdx;
        return (
          <div className="cs-modal-overlay" onClick={() => !placing && setConfirmBetIdx(null)}>
            <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Place this bet on Betfair?</h3>
              <p className="cs-confirm-warn">
                <span className="cs-warn-icon" aria-hidden>⚠</span>
                <span>
                  This places a <strong>real-money {b.side}</strong> bet on your connected Betfair
                  account. It cannot be undone from here.
                </span>
              </p>
              <div className="cs-confirm-summary">
                <div className="cs-cs-row">
                  <span>Runner</span>
                  <strong>{b.runner}</strong>
                </div>
                <div className="cs-cs-row">
                  <span>Race</span>
                  <strong>{b.track} · R{b.race}</strong>
                </div>
                <div className="cs-cs-row">
                  <span>Side</span>
                  <strong><span className={`cs-bet-side ${b.side}`}>{b.side}</span></strong>
                </div>
                <div className="cs-cs-row">
                  <span>Price</span>
                  <strong>
                    {effPrice(b, confirmBetIdx) ?? '—'}
                    {edits[confirmBetIdx]?.price ? <span className="cs-edited-tag"> (edited)</span> : null}
                  </strong>
                </div>
                <div className="cs-cs-row">
                  <span>Stake</span>
                  <strong>
                    {fmtMoney(effStake(b, confirmBetIdx))}
                    {edits[confirmBetIdx]?.stake ? <span className="cs-edited-tag"> (edited)</span> : null}
                    {committedBank > 0 && (
                      <span className="cs-stake-basis"> · {((effStake(b, confirmBetIdx) / committedBank) * 100).toFixed(1)}% of today's bank ({fmtMoney(committedBank)})</span>
                    )}
                  </strong>
                </div>
                <div className="cs-cs-row">
                  <span>If unmatched at the off</span>
                  <label className="cs-take-sp">
                    <input
                      type="checkbox"
                      checked={takeSP}
                      onChange={(e) => setTakeSP(e.target.checked)}
                      disabled={placing}
                    />
                    <span>{takeSP ? 'Take SP (starting price)' : 'Cancel unmatched (lapse)'}</span>
                  </label>
                </div>
              </div>
              {confirmErr && (
                <div className="cs-modal-error" role="alert">
                  <strong>Couldn't place this bet:</strong> {confirmErr}
                </div>
              )}
              <div className="cs-modal-actions">
                <button className="cs-modal-cancel" onClick={() => setConfirmBetIdx(null)} disabled={placing}>
                  Cancel
                </button>
                <button className="cs-confirm-place" onClick={() => onConfirm(confirmBetIdx)} disabled={placing}>
                  {placing ? (
                    <>
                      <span className="cs-spinner" /> Placing…
                    </>
                  ) : (
                    `Place ${fmtMoney(effStake(b, confirmBetIdx))} bet`
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {confirmRebuild && run && (
        <div className="cs-modal-overlay" onClick={() => !building && setConfirmRebuild(false)}>
          <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Re-run today's strategy?</h3>
            <p className="cs-confirm-warn">
              <span className="cs-warn-icon" aria-hidden>⚠</span>
              <span>
                This <strong>replaces today's picks</strong> with a fresh run over the whole card and
                clears any discussion on this run. Use it only when the fields or prices have changed
                (e.g. scratchings). Bets already placed on Betfair are <strong>not</strong> affected.
              </span>
            </p>
            <div className="cs-modal-actions">
              <button className="cs-modal-cancel" onClick={() => setConfirmRebuild(false)} disabled={building}>
                Cancel
              </button>
              <button className="cs-modal-delete" onClick={() => onBuild(true)} disabled={building}>
                {building ? (
                  <>
                    <span className="cs-spinner" /> Re-running…
                  </>
                ) : (
                  "Re-run & replace"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteId && (
        <div
          className="cs-modal-overlay"
          onClick={() => !deleting && setConfirmDeleteId(null)}
        >
          <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete this run?</h3>
            <p>
              This removes the run's picks, runner log and any discussion. Bets already placed on Betfair
              are <strong>not</strong> affected.
            </p>
            <div className="cs-modal-actions">
              <button className="cs-modal-cancel" onClick={() => setConfirmDeleteId(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="cs-modal-delete" onClick={doDelete} disabled={deleting}>
                {deleting ? (
                  <>
                    <span className="cs-spinner" /> Deleting…
                  </>
                ) : (
                  'Delete run'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reads strategy_runner_log directly, so it does NOT depend on a run being loaded above:
          the whole point is to show what happened on days and races the slate does not mention. */}
      <RaceDetailTable date={date} />
    </div>
  );
};

export default ClaudeStrategy;
